// Shared web-notifications + sound helpers.
// Used by chat.ts (DM + @mention triggers), call-session.ts (ringtone /
// incoming-call popups), and the Settings Notifications tab.
//
// v1 only covers *foreground-tab* notifications via the browser Notification
// API. Real web-push (service worker + VAPID + backend subscription storage)
// is deferred to batch 9+.

import { state } from '../state';

type NotifyKind = 'dm' | 'mention' | 'call' | 'reply' | 'sms' | 'mms' | 'message';

const MUTE_KEY = 'hub-people-muted-channels';
const SERVER_MUTE_KEY = 'hub-people-server-notifs';
const MASTER_KEY = 'hub-people-notifs-master';
const SOUND_KEY = 'hub-people-sound';
const BANNER_DISMISSED_KEY = 'hub-people-notifs-banner-dismissed';

/** Per-server notification mode: 'all' (default), 'mentions' (only @mentions/replies), 'none' (muted). */
export type ServerNotifMode = 'all' | 'mentions' | 'none';

function loadServerModes(): Record<string, ServerNotifMode> {
  try {
    const raw = localStorage.getItem(SERVER_MUTE_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw) as unknown;
    if (obj && typeof obj === 'object') {
      const out: Record<string, ServerNotifMode> = {};
      for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
        if (v === 'all' || v === 'mentions' || v === 'none') out[k] = v;
      }
      return out;
    }
  } catch { /* ignore */ }
  return {};
}
function saveServerModes(modes: Record<string, ServerNotifMode>): void {
  try { localStorage.setItem(SERVER_MUTE_KEY, JSON.stringify(modes)); } catch { /* ignore */ }
}
export function getServerNotifMode(serverId: string): ServerNotifMode {
  return loadServerModes()[serverId] ?? 'all';
}
export function setServerNotifMode(serverId: string, mode: ServerNotifMode): void {
  const m = loadServerModes();
  if (mode === 'all') delete m[serverId];
  else m[serverId] = mode;
  saveServerModes(m);
}
export function getAllServerNotifModes(): Record<string, ServerNotifMode> {
  return loadServerModes();
}

export function isNotificationsMasterOn(): boolean {
  return localStorage.getItem(MASTER_KEY) !== 'false';
}
export function setNotificationsMasterOn(on: boolean): void {
  try { localStorage.setItem(MASTER_KEY, String(on)); } catch { /* ignore */ }
}

export function getMutedChannels(): string[] {
  try {
    const raw = localStorage.getItem(MUTE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as unknown;
    if (Array.isArray(arr)) return arr.filter((x): x is string => typeof x === 'string');
  } catch { /* ignore */ }
  return [];
}
export function isChannelMuted(channel: string): boolean {
  return getMutedChannels().includes(channel);
}
export function toggleChannelMute(channel: string): boolean {
  const list = new Set(getMutedChannels());
  if (list.has(channel)) list.delete(channel); else list.add(channel);
  try { localStorage.setItem(MUTE_KEY, JSON.stringify([...list])); } catch { /* ignore */ }
  return list.has(channel);
}
export function unmuteChannel(channel: string): void {
  const list = new Set(getMutedChannels());
  list.delete(channel);
  try { localStorage.setItem(MUTE_KEY, JSON.stringify([...list])); } catch { /* ignore */ }
}

export type PermState = 'granted' | 'denied' | 'default' | 'unsupported';

export function getPermissionStatus(): PermState {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

export async function requestNotificationPermission(): Promise<PermState> {
  if (!('Notification' in window)) return 'unsupported';
  try {
    const p = await Notification.requestPermission();
    return p as PermState;
  } catch {
    return 'denied';
  }
}

// Gesture-synchronous variant. Android Chrome requires the
// Notification.requestPermission() call to happen in the same synchronous
// stack as the user's pointerup/click. Wrapping in an async function or
// routing through a microtask chain (setTimeout, .then in another handler)
// causes Chrome to silently drop the prompt. Call this one directly from
// inside the click listener — before any await — and receive the outcome
// via the onResult callback.
export function requestNotificationPermissionSync(onResult?: (p: PermState) => void): void {
  if (!('Notification' in window)) { onResult?.('unsupported'); return; }
  try {
    // MUST be called synchronously — no awaits, setTimeouts, or microtasks
    // before this line. The return value is a Promise (modern browsers) or
    // undefined with a legacy callback argument (older Safari).
    const result = Notification.requestPermission((legacyP) => {
      onResult?.(legacyP as PermState);
    });
    if (result && typeof (result as Promise<NotificationPermission>).then === 'function') {
      (result as Promise<NotificationPermission>)
        .then((p) => onResult?.(p as PermState))
        .catch(() => onResult?.('denied'));
    }
  } catch {
    onResult?.('denied');
  }
}

const FIRST_DM_PROMPT_KEY = 'hub-people-first-dm-prompt-shown';

// Call from inside a gesture stack (e.g. the click/Enter that triggers a
// DM send) to prompt once on the user's first outgoing DM. Skips when
// permission has already resolved, when the tab was previously prompted,
// or when notifications are unsupported.
export function maybePromptOnFirstDmSend(): void {
  if (!('Notification' in window)) return;
  if (Notification.permission !== 'default') return;
  try { if (localStorage.getItem(FIRST_DM_PROMPT_KEY) === '1') return; } catch { /* ignore */ }
  try { localStorage.setItem(FIRST_DM_PROMPT_KEY, '1'); } catch { /* ignore */ }
  requestNotificationPermissionSync();
}

// Show a subtle, dismissable banner asking the user to enable notifications.
// Never auto-called without a user gesture elsewhere — call sites should
// mount this on their own schedule (e.g. after first message render).
export function showPermissionBannerIfNeeded(): void {
  if (!('Notification' in window)) return;
  if (Notification.permission !== 'default') return;
  try { if (localStorage.getItem(BANNER_DISMISSED_KEY) === '1') return; } catch { /* ignore */ }
  if (document.getElementById('hp-notif-banner')) return;

  const banner = document.createElement('div');
  banner.id = 'hp-notif-banner';
  banner.className = 'hp-notif-banner';
  banner.setAttribute('role', 'dialog');
  banner.setAttribute('aria-label', 'Enable notifications');
  banner.innerHTML = `
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5S10.5 3.17 10.5 4v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/></svg>
    <span class="hp-notif-banner-text">Get notified when you're mentioned</span>
    <button class="hp-notif-banner-btn hp-notif-banner-enable" type="button">Enable</button>
    <button class="hp-notif-banner-btn hp-notif-banner-close" type="button">Not now</button>
  `;
  document.body.appendChild(banner);

  const close = (): void => { banner.remove(); };
  banner.querySelector('.hp-notif-banner-close')?.addEventListener('click', () => {
    try { localStorage.setItem(BANNER_DISMISSED_KEY, '1'); } catch { /* ignore */ }
    close();
  });
  banner.querySelector('.hp-notif-banner-enable')?.addEventListener('click', () => {
    // Sync-first: fire the prompt inside the gesture stack before any await.
    requestNotificationPermissionSync(() => close());
  });
}

function playSoundIfOn(kind: NotifyKind): void {
  if (localStorage.getItem(SOUND_KEY) === 'false') return;
  try {
    const AC = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext
      ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = kind === 'call' ? 440 : 880;
    gain.gain.value = 0.05;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.18);
    osc.onended = (): void => { try { void ctx.close(); } catch { /* noop */ } };
  } catch { /* ignore — audio may require user gesture */ }
}

// Incoming-call ringtone: short tone looped every 2s until stopRingtone() is
// called (e.g. on answer, decline, or missed).
let ringtoneCtx: AudioContext | null = null;
let ringtoneTimer: ReturnType<typeof setInterval> | null = null;

export function startRingtone(): void {
  if (localStorage.getItem(SOUND_KEY) === 'false') return;
  stopRingtone();
  try {
    const AC = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext
      ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    ringtoneCtx = new AC();
    const pulse = (): void => {
      if (!ringtoneCtx) return;
      const ctx = ringtoneCtx;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 520;
      gain.gain.setValueAtTime(0.0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.07, ctx.currentTime + 0.05);
      gain.gain.linearRampToValueAtTime(0.0, ctx.currentTime + 1.0);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 1.0);
    };
    pulse();
    ringtoneTimer = setInterval(pulse, 2000);
  } catch { /* ignore */ }
}
export function stopRingtone(): void {
  if (ringtoneTimer) { clearInterval(ringtoneTimer); ringtoneTimer = null; }
  if (ringtoneCtx) { try { void ringtoneCtx.close(); } catch { /* noop */ } ringtoneCtx = null; }
}

type NotifyOpts = {
  kind: NotifyKind;
  title: string;
  body: string;
  // `channel` is the channel key used for the mute list (e.g. the channel
  // name). Mentions fire even when muted (Discord parity); DMs + calls
  // ignore mute because they don't map to a regular channel.
  channel?: string;
  // Server the event belongs to — used to consult per-server mode. DMs and
  // calls pass no serverId and bypass server gating.
  serverId?: string;
  icon?: string;
  onClick?: () => void;
  // Whether the user is actively looking at this surface right now. When set it
  // REPLACES the coarse `document.hidden` check: true → suppress the popup (they
  // can already see it), false → fire even if the tab is visible (they're on a
  // different channel). Omitted → falls back to `!document.hidden` (back-compat
  // for the DM/mention/call callers).
  active?: boolean;
};

// Fire a browser notification + optional sound, gated by:
// 1. Master toggle off → nothing at all
// 2. Server mode 'none' → nothing; 'mentions' → only mention/reply kinds
// 3. Per-channel mute + kind !== 'mention' → skip (except for calls)
// 4. Tab visible → skip the browser popup but still play the sound
// 5. Permission not granted → skip popup
export function notify(opts: NotifyOpts): void {
  if (!isNotificationsMasterOn()) return;

  if (opts.serverId) {
    const mode = getServerNotifMode(opts.serverId);
    if (mode === 'none') return;
    if (mode === 'mentions' && opts.kind !== 'mention' && opts.kind !== 'reply') return;
  }

  const muted = opts.channel && isChannelMuted(opts.channel);
  if (muted && opts.kind !== 'mention' && opts.kind !== 'call') return;

  playSoundIfOn(opts.kind);

  // Suppress the popup only when the user is actively viewing this surface.
  // `active` (per-channel) wins when provided; otherwise: fire UNLESS this
  // window is the visible, FOCUSED one — i.e. fire whenever it isn't the active
  // window (another app/tab, backgrounded, or the phone locked). (Mitchell.)
  const activelyViewing = opts.active ?? (!document.hidden && windowFocused());
  if (activelyViewing) return;
  if (getPermissionStatus() !== 'granted') return;

  showSystemNotification(opts);
}

/** Best-effort "is this window the focused one". */
function windowFocused(): boolean {
  try { return document.hasFocus(); } catch { return true; }
}

/** Fire the actual OS/Chrome notification. Prefers the Service Worker
 *  registration — REQUIRED on Android Chrome, where `new Notification()` throws
 *  ("Illegal constructor"). Falls back to the page constructor only where no SW
 *  controls the page (older desktop). This is why notifications never showed on
 *  mobile before. NOTE: while the app is fully CLOSED on a phone this still
 *  won't fire — that needs real Web Push (a push subscription + server push via
 *  hub-notify); the SW already has the `push` + `notificationclick` handlers,
 *  the subscription + server side is the deferred piece. */
function showSystemNotification(opts: NotifyOpts): void {
  const nOpts: NotificationOptions & { data?: unknown } = {
    body: opts.body.slice(0, 180),
    icon: opts.icon ?? '/people/icon-192.png',
    badge: '/people/icon-192.png',
    tag: `hp-${opts.kind}`,
    data: { url: '/people/' },
  };
  if (!('serviceWorker' in navigator)) { legacyNotify(opts.title, nOpts, opts.onClick); return; }
  // Race `.ready` against a short timeout so a never-resolving registration
  // (rare failure) still falls back instead of silently dropping the popup.
  const timeout = new Promise<null>((res) => setTimeout(() => res(null), 800));
  Promise.race([navigator.serviceWorker.ready, timeout])
    .then((reg) => {
      if (reg) (reg as ServiceWorkerRegistration).showNotification(opts.title, nOpts)
        .catch(() => legacyNotify(opts.title, nOpts, opts.onClick));
      else legacyNotify(opts.title, nOpts, opts.onClick);
    })
    .catch(() => legacyNotify(opts.title, nOpts, opts.onClick));
}

function legacyNotify(title: string, nOpts: NotificationOptions, onClick?: () => void): void {
  try {
    const n = new Notification(title, nOpts);
    n.onclick = (): void => {
      try { window.focus(); } catch { /* noop */ }
      n.close();
      onClick?.();
    };
  } catch { /* mobile / unsupported — the SW path is the primary route */ }
}

/** True when the user is actively looking at (serverId, channel) right now: the
 *  app is visible + focused AND that exact channel is on screen. Used to
 *  suppress notifications (and the enable-push nudge) for the channel you're
 *  already reading. App hidden or a different channel → NOT active → notify. */
function isChannelActive(serverId: string, channel: string): boolean {
  // Active = this window is the FOCUSED, visible one AND showing this exact
  // channel. Blurred (another app/tab/window), hidden/backgrounded, phone
  // locked, or a different channel → NOT active → the notification fires
  // (Mitchell: fire whenever this isn't the active window on the device).
  if (document.hidden || !windowFocused()) return false;
  const curServer = state.currentServerId ?? state.currentServer;
  return state.currentChannel === channel && curServer === serverId;
}

/** Handle an incoming realtime message. Fires a web notification UNLESS the
 *  message is the user's own (no self-spam) or the channel is actively on
 *  screen. Returns true when the message was "notifiable" (someone else, a
 *  channel not currently active) so callers can gate one-time prompts on the
 *  same rule. Delivery still honours the master toggle + per-server mode +
 *  per-channel mute inside notify(). NOTE: a fully-closed mobile app needs real
 *  background Web Push (deferred to hub-notify) — this covers the app while its
 *  realtime stream is alive (foreground, or a backgrounded-but-connected PWA). */
export function notifyForMessage(msg: { author_id: string; server_id: string; channel: string; content: string }): boolean {
  const myId = state.ownerId || state.people[0]?.id || '';
  if (myId && msg.author_id === myId) return false;                // my own message → never
  // sms/mms get a specialised popup via global-notifier — don't double-notify.
  if (msg.channel === 'sms' || msg.channel === 'mms') return false;
  if (isChannelActive(msg.server_id, msg.channel)) return false;   // I'm looking at it → never
  const author = state.people.find((p) => p.id === msg.author_id)?.name;
  notify({
    kind: 'message',
    title: author ? `${author} · #${msg.channel}` : `#${msg.channel}`,
    body: msg.content && msg.content.trim() ? msg.content : 'New message',
    channel: msg.channel,
    serverId: msg.server_id,
    active: false, // already established the channel is not on screen
  });
  return true;
}
